import { parseUtcDateTime } from './dateHelper.js';

const pickerOrigins = new WeakMap();

/**
 * PrimeVue DatePicker with show-time reads Date local getters (getHours, …).
 * API instants are UTC; list/KPI formatters use resolveDisplayTimezone().
 * Bind pickers through these helpers so the digits match formatDateTimeShort.
 */

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number, second: number }}
 */
export function zonedDateTimeParts(date, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const map = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour) % 24,
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

/**
 * Calendar-day difference in a timezone (to midnight, not browser local).
 * @param {Date} fromDate
 * @param {Date} toDate
 * @param {string} timeZone
 * @returns {number}
 */
export function zonedCalendarDayDiff(fromDate, toDate, timeZone) {
  const from = zonedDateTimeParts(fromDate, timeZone);
  const to = zonedDateTimeParts(toDate, timeZone);
  const fromUtc = Date.UTC(from.year, from.month - 1, from.day);
  const toUtc = Date.UTC(to.year, to.month - 1, to.day);
  return Math.round((toUtc - fromUtc) / 86400000);
}

/**
 * Today's calendar date in $timeZone (YYYY-MM-DD). Not browser local, not UTC date from toISOString.
 * @param {string} timeZone
 * @param {Date} [now]
 * @returns {string}
 */
export function zonedTodayYmd(timeZone, now = new Date()) {
  const p = zonedDateTimeParts(now, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * UTC instant (API Date/ISO) → Date whose local components equal display-TZ wall clock.
 * @param {string|Date|null|undefined} value
 * @param {string} timeZone
 * @returns {Date|null}
 */
export function utcInstantToPickerDate(value, timeZone) {
  if (value == null || value === '') return null;
  let instant = null;
  if (value instanceof Date) {
    instant = Number.isNaN(value.getTime()) ? null : value;
  } else {
    instant = parseUtcDateTime(value);
  }
  if (!instant) return null;
  const p = zonedDateTimeParts(instant, timeZone);
  const picker = new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, 0);
  // A browser-local Date cannot represent a clock skipped by its own DST transition.
  if (picker.getHours() !== p.hour || picker.getDate() !== p.day || picker.getMinutes() !== p.minute) return null;
  pickerOrigins.set(picker, { timeZone, clock: picker.getTime(), instant: instant.getTime() });
  return picker;
}

/**
 * DatePicker Date (local getters = display-TZ wall clock) → real UTC instant.
 * @param {Date|null|undefined} pickerDate
 * @param {string} timeZone
 * @returns {Date|null}
 */
export function pickerDateToUtcInstant(pickerDate, timeZone) {
  if (!(pickerDate instanceof Date) || Number.isNaN(pickerDate.getTime())) return null;
  const origin = pickerOrigins.get(pickerDate);
  if (origin && origin.timeZone === timeZone && origin.clock === pickerDate.getTime()) {
    return new Date(origin.instant);
  }
  return wallClockInZoneToUtcDate(
    pickerDate.getFullYear(),
    pickerDate.getMonth() + 1,
    pickerDate.getDate(),
    pickerDate.getHours(),
    pickerDate.getMinutes(),
    pickerDate.getSeconds(),
    timeZone
  );
}

function wallClockInZoneToUtcDate(year, month, day, hour, minute, second, timeZone) {
  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  const asUtcMs = (parts) =>
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  // Probe both sides of a transition. A skipped clock has no matching instant;
  // a repeated clock has two. New selections use the earlier occurrence.
  const offsets = new Set();
  for (const delta of [-48, -24, 0, 24, 48]) {
    const probe = target + delta * 3600000;
    offsets.add(asUtcMs(zonedDateTimeParts(new Date(probe), timeZone)) - probe);
  }
  const matches = [...offsets]
    .map((offset) => target - offset)
    .filter((ms) => asUtcMs(zonedDateTimeParts(new Date(ms), timeZone)) === target)
    .sort((a, b) => a - b);
  return matches.length ? new Date(matches[0]) : null;
}
